import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import apiService from '../../services/api';
import logger from '../../utils/logging/logger';

export default function useHeader() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotificationPanel, setShowNotificationPanel] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);

  // Fetch notification count
  const fetchNotificationCount = async () => {
    try {
      const response = await apiService.getNotifications();
      if (response.success) {
        const unreadCount = response.notifications?.filter(n => !n.is_read).length || 0;
        setNotificationCount(unreadCount);
      }
    } catch (error) {
      logger.error('Error fetching notification count', { error });
      // For demo purposes, set a mock count
      setNotificationCount(3);
    }
  };

  useEffect(() => {
    if (user) {
      fetchNotificationCount();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchNotificationCount, 30000);
      return () => clearInterval(interval);
    } else {
      // Clear notification count when user is null
      setNotificationCount(0);
    }
  }, [user]);

  // Close panels when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotificationPanel || showUserMenu) {
        const target = event.target;
        const isNotificationPanel = target.closest('[data-notification-panel]');
        const isUserMenu = target.closest('[data-user-menu]');
        const isNotificationButton = target.closest('[data-notification-button]');
        const isUserButton = target.closest('[data-user-button]');

        if (!isNotificationPanel && !isNotificationButton && showNotificationPanel) {
          setShowNotificationPanel(false);
        }

        if (!isUserMenu && !isUserButton && showUserMenu) {
          setShowUserMenu(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotificationPanel, showUserMenu]);

  const handleLogout = async () => {
    setShowUserMenu(false);
    setShowNotificationPanel(false);
    // Set logged out flag BEFORE calling logout API to ensure it's set even if API fails
    localStorage.setItem('faithinsight_logged_out', 'true');
    await logout();
    navigate('/login');
  };

  const handleProfileClick = () => {
    navigate('/profile');
    setShowUserMenu(false);
  };

  const handleSettingsClick = () => {
    navigate('/settings');
    setShowUserMenu(false);
  };

  const handleNotificationClick = () => {
    setShowNotificationPanel(!showNotificationPanel);
    setShowUserMenu(false);
  };

  const handleNotificationClose = () => {
    setShowNotificationPanel(false);
  };

  const handleNotificationUpdate = () => {
    fetchNotificationCount();
  };

  const handleLogoClick = () => {
    // Navigate to dashboard based on user role
    if (user?.role === 'admin') {
      navigate('/admin/dashboard');
    } else if (user?.role === 'instructor') {
      navigate('/instructor/dashboard');
    } else if (user?.role === 'student') {
      navigate('/student/dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  return {
    user,
    theme,
    toggleTheme,
    showUserMenu,
    setShowUserMenu,
    showNotificationPanel,
    setShowNotificationPanel,
    notificationCount,
    handleLogout,
    handleProfileClick,
    handleSettingsClick,
    handleNotificationClick,
    handleNotificationClose,
    handleNotificationUpdate,
    handleLogoClick
  };
}
